import { useState } from 'preact/hooks';
import { Lightbulb, X, Sparkles } from 'lucide-preact';
import { Modal } from '@/components/Modal';
import { Pill } from '@/components/Pill';
import { useFetch } from '@/lib/useFetch';
import { apiPost } from '@/lib/api';
import { pushToast } from '@/lib/toasts';

export interface AgentSuggestion {
  id: number;
  from_agent: string;
  suggested_id: string;
  suggested_name: string;
  suggested_description: string;
  reasoning: string;
  activity_share_pct: number | null;
  created_at: number;
}

// Polled by AgentCard so each card knows whether to show a lightbulb.
// Also consumed by a refresh button in the Insights drawer.
export function useAgentSuggestions() {
  return useFetch<{ suggestions: AgentSuggestion[] }>('/api/agents/suggestions', 60_000);
}

interface DetailProps {
  suggestion: AgentSuggestion | null;
  onClose: () => void;
  onActed: (s: AgentSuggestion) => void; // open the wizard pre-filled
  onChange: () => void;                  // refresh the list after dismiss
}

export function AgentSuggestionModal({ suggestion, onClose, onActed, onChange }: DetailProps) {
  const [busy, setBusy] = useState(false);
  if (!suggestion) return null;

  async function dismiss() {
    setBusy(true);
    try {
      await apiPost(`/api/agents/suggestions/${suggestion!.id}/dismiss`);
      pushToast({ tone: 'warn', title: 'Suggestion dismissed' });
      onChange();
      onClose();
    } catch (err: any) {
      pushToast({ tone: 'error', title: 'Dismiss failed', description: err?.message || String(err) });
    } finally { setBusy(false); }
  }

  function act() {
    // Mark as acted on the server then ask the parent (Agents page) to
    // open the create wizard pre-filled with the suggested values.
    void apiPost(`/api/agents/suggestions/${suggestion.id}/acted`).catch(() => {});
    onActed(suggestion);
  }

  const share = suggestion.activity_share_pct ?? 0;
  return (
    <Modal
      open
      onClose={onClose}
      title="Spin off a specialized agent?"
      width={540}
      footer={
        <>
          <button
            type="button"
            onClick={dismiss}
            disabled={busy}
            class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-[12px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] border border-[var(--color-border)] transition-colors disabled:opacity-40"
          >
            <X size={12} /> {busy ? 'Dismissing…' : 'Dismiss'}
          </button>
          <button
            type="button"
            onClick={act}
            class="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[12px] font-medium bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)] transition-colors"
          >
            <Sparkles size={13} /> Open wizard with these values
          </button>
        </>
      }
    >
      <div class="space-y-3">
        <div class="text-[12.5px] text-[var(--color-text-muted)] leading-relaxed">
          <Pill tone="neutral">@{suggestion.from_agent}</Pill> is doing several things — splitting one off into a focused agent could clean up its scope.
        </div>

        <div class="bg-[var(--color-elevated)] border border-[var(--color-border)] rounded-lg p-3 space-y-2">
          <div class="flex items-center gap-2">
            <Lightbulb size={14} class="text-[var(--color-accent)]" />
            <span class="text-[14px] font-semibold text-[var(--color-text)]">{suggestion.suggested_name}</span>
            <code class="text-[10.5px] text-[var(--color-text-faint)] font-mono">{suggestion.suggested_id}</code>
            {share > 0 && (
              <span class="ml-auto text-[10.5px] text-[var(--color-text-faint)] tabular-nums">
                ~{share}% of @{suggestion.from_agent}'s activity
              </span>
            )}
          </div>
          <div class="text-[12.5px] text-[var(--color-text)] leading-snug">
            {suggestion.suggested_description}
          </div>
        </div>

        <div>
          <div class="text-[10px] uppercase tracking-wider text-[var(--color-text-faint)] mb-1">Why now</div>
          <div class="text-[12px] text-[var(--color-text-muted)] leading-relaxed">
            {suggestion.reasoning}
          </div>
        </div>

        <div class="text-[11px] text-[var(--color-text-faint)] border-t border-[var(--color-border)] pt-2.5">
          Generated by Haiku from this agent's last 200 hive_mind entries. You'll still need a fresh BotFather token in step 2 of the wizard.
        </div>
      </div>
    </Modal>
  );
}

interface BadgeProps {
  agentId: string;
  suggestions: AgentSuggestion[];
  onOpen: (s: AgentSuggestion) => void;
}

// Small lightbulb shown next to an AgentCard's name when there's an
// active suggestion targeting that agent. Click → open the modal.
export function AgentSuggestionBadge({ agentId, suggestions, onOpen }: BadgeProps) {
  const match = suggestions.find((s) => s.from_agent === agentId);
  if (!match) return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpen(match); }}
      class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[var(--color-accent-soft)] text-[var(--color-accent)] hover:bg-[var(--color-accent)] hover:text-white transition-colors"
      title={`Suggestion: spin off "${match.suggested_name}"`}
      aria-label="Agent split suggestion"
    >
      <Lightbulb size={11} />
    </button>
  );
}
